import { cx } from "../../lib/cx.js";
import { Button } from "../Button/Button.js";
import { focusFirst } from "../_overlay/focus.js";
import { IconButton } from "../IconButton/IconButton.js";
/* empty css                         */
import { useId, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/RepeatableFieldGroup/RepeatableFieldGroup.tsx
/**
* Fields the user can repeat: add a row, remove a row (a user's typed external IDs).
* After adding, focus moves to the new row's first field; after removing, to the previous
* row (or Add). Both are announced.
*/
function RepeatableFieldGroup({ legend, rows, onChange, createRow, renderRow, itemLabel = "Item", rowLabel = (_row, index) => `${itemLabel} ${index + 1}`, addLabel = `Add ${itemLabel.charAt(0).toLowerCase()}${itemLabel.slice(1)}`, min = 0, max, maxReason = `You can add up to ${max}.`, getKey, error, disabled = false, className }) {
	const id = useId();
	const rowsRef = useRef(null);
	const addRef = useRef(null);
	const [announcement, setAnnouncement] = useState("");
	const [keys, setKeys] = useState(() => ({
		list: rows.map((_, i) => `row${i}`),
		next: rows.length
	}));
	const keyOf = (row, index) => getKey?.(row, index) ?? keys.list[index] ?? `extra${index}`;
	/** After React renders, focuses the first field of the row at `index`, else Add. */
	const focusRowLater = (index) => {
		requestAnimationFrame(() => {
			const row = rowsRef.current?.children[index];
			if (row) focusFirst(row);
			else addRef.current?.focus();
		});
	};
	const atMax = max !== void 0 && rows.length >= max;
	const canRemove = rows.length > min;
	const add = () => {
		const next = [...rows, createRow()];
		setKeys(({ list, next: n }) => ({
			list: [...list.slice(0, rows.length), `row${n}`],
			next: n + 1
		}));
		onChange(next);
		setAnnouncement(`${rowLabel(next.at(-1), next.length - 1)} added`);
		focusRowLater(next.length - 1);
	};
	const remove = (index) => {
		const label = rowLabel(rows[index], index);
		setKeys(({ list, next }) => ({
			list: list.filter((_, i) => i !== index),
			next
		}));
		onChange(rows.filter((_, i) => i !== index));
		setAnnouncement(`${label} removed`);
		focusRowLater(index - 1);
	};
	const update = (index) => (row) => onChange(rows.map((current, i) => i === index ? row : current));
	return /* @__PURE__ */ jsxs("fieldset", {
		className: cx("ds-repeatable", className),
		disabled,
		"aria-describedby": error ? `${id}error` : void 0,
		children: [
			/* @__PURE__ */ jsx("legend", {
				className: "ds-repeatable__legend",
				children: legend
			}),
			error && /* @__PURE__ */ jsx("p", {
				id: `${id}error`,
				className: "ds-repeatable__error",
				children: error
			}),
			rows.length > 0 && /* @__PURE__ */ jsx("ol", {
				ref: rowsRef,
				className: "ds-repeatable__rows",
				children: rows.map((row, index) => /* @__PURE__ */ jsxs("li", {
					className: "ds-repeatable__row",
					children: [/* @__PURE__ */ jsx("div", {
						className: "ds-repeatable__fields",
						children: renderRow(row, index, update(index))
					}), canRemove && /* @__PURE__ */ jsx("span", {
						className: "ds-repeatable__remove",
						children: /* @__PURE__ */ jsx(IconButton, {
							icon: "trash",
							variant: "ghost",
							"aria-label": `Remove ${rowLabel(row, index)}`,
							onClick: () => remove(index)
						})
					})]
				}, keyOf(row, index)))
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "ds-repeatable__add",
				children: [/* @__PURE__ */ jsx(Button, {
					ref: addRef,
					variant: "secondary",
					size: "sm",
					leadingIcon: "plus",
					disabled: atMax,
					"aria-describedby": atMax ? `${id}max` : void 0,
					onClick: add,
					children: addLabel
				}), atMax && /* @__PURE__ */ jsx("span", {
					id: `${id}max`,
					className: "ds-repeatable__reason",
					children: maxReason
				})]
			}),
			/* @__PURE__ */ jsx("span", {
				className: "ds-repeatable__status",
				role: "status",
				children: announcement
			})
		]
	});
}
//#endregion
export { RepeatableFieldGroup };

//# sourceMappingURL=RepeatableFieldGroup.js.map